/**
 * SaveQueue - Serializes all application save operations (autosave + page-complete)
 * through a single queue to prevent race conditions on revision conflicts.
 *
 * Key behaviors:
 * - Serial execution: only one API call at a time
 * - Collapsible autosave: consecutive autosave entries collapse into the latest
 * - Non-collapsible pageComplete: always executes, never dropped
 * - Revision chaining: each operation receives the latest revision from the prior success
 * - 409 conflict recovery: retries once after fetching the latest revision from the server
 */

const CONFLICT_STATUS = 409;
const MAX_RETRIES = 1;

class SaveQueue {
    constructor() {
        this._queue = [];
        this._processing = false;
        this._revision = null;
        this._flushResolvers = [];
        this._fetchLatestRevision = null; // Injected function to fetch latest revision from server
    }

    setRevision(rev) {
        this._revision = rev;
    }

    getRevision() {
        return this._revision;
    }

    /**
   * Inject a function that fetches the latest application revision from the server.
   * Used for 409 conflict recovery.
   * @param {() => Promise<number>} fn - returns the latest revision number
   */
    setConflictResolver(fn) {
        this._fetchLatestRevision = fn;
    }

    enqueue(operation) {
        if (operation.type === 'autosave') {
            // Collapse consecutive autosave entries - replace the last one if it's also autosave
            const lastIndex = this._queue.length - 1;
            if (lastIndex >= 0 && this._queue[lastIndex].type === 'autosave') {
                this._queue[lastIndex] = operation;
                return;
            }
        }

        this._queue.push(operation);
        this._processNext();
    }

    async flush() {
        if (!this._processing && this._queue.length === 0) {
            return;
        }
        return new Promise((resolve) => {
            this._flushResolvers.push(resolve);
        });
    }

    clear() {
        this._queue = [];
        this._processing = false;
        this._resolveFlush();
    }

    async _processNext() {
        if (this._processing) {
            return;
        }

        if (this._queue.length === 0) {
            this._resolveFlush();
            return;
        }

        this._processing = true;
        const operation = this._queue.shift();

        try {
            const result = await this._executeWithRetry(operation, 0);
            if (result && result.revision != null) {
                this._revision = result.revision;
            }
            if (operation.onSuccess) {
                operation.onSuccess(result);
            }
        } catch (error) {
            if (operation.onError) {
                operation.onError(error);
            }
        } finally {
            this._processing = false;
            this._processNext();
        }
    }

    async _executeWithRetry(operation, attempt) {
        try {
            return await operation.execute(this._revision);
        } catch (error) {
            const isConflict = error?.errorCode === CONFLICT_STATUS || error?.status === CONFLICT_STATUS;
            if (isConflict && attempt < MAX_RETRIES && this._fetchLatestRevision) {
                // Fetch latest revision from server and retry
                const latestRevision = await this._fetchLatestRevision();
                if (latestRevision != null) {
                    this._revision = latestRevision;
                }
                return this._executeWithRetry(operation, attempt + 1);
            }
            throw error;
        }
    }

    _resolveFlush() {
        const resolvers = this._flushResolvers;
        this._flushResolvers = [];
        resolvers.forEach((resolve) => resolve());
    }
}

const saveQueue = new SaveQueue();

export { SaveQueue, saveQueue };
